import React, { useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Images, Upload, CheckCircle2, AlertTriangle, Loader2, X, FolderUp } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Normalise "MH12-AB-1234" → "MH12AB1234" (case-insensitive)
const norm = (s) => (s || "").toString().toUpperCase().replace(/[^A-Z0-9]/g, "");

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

/**
 * Bulk-attach photos to existing vehicles by matching file names to vehicle numbers.
 * Example filenames that all match "MH-12-AB-1234":  MH12AB1234.jpg, MH 12 AB 1234.png, mh12ab1234.webp
 */
export default function BulkVehiclePhotosDialog({ open, onOpenChange, vehicles, onDone }) {
  const [items, setItems] = useState([]); // [{file, dataUrl, matchedVehicle|null}]
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {saved, skipped, failed[]}
  const fileRef = useRef(null);

  const byNorm = useMemo(() => {
    const m = new Map();
    (vehicles || []).forEach(v => m.set(norm(v.vehicle_number), v));
    return m;
  }, [vehicles]);

  const reset = () => { setItems([]); setResult(null); };

  const pickFiles = async (fileList) => {
    reset();
    const files = Array.from(fileList || []).filter(f => f.type.startsWith("image/"));
    if (files.length === 0) { toast.error("Pick image files"); return; }
    if (files.length > 100) { toast.error("Max 100 photos per batch"); return; }
    const next = [];
    for (const f of files) {
      if (f.size > 3 * 1024 * 1024) { next.push({ file: f, dataUrl: null, matchedVehicle: null, tooBig: true }); continue; }
      const dataUrl = await readAsDataUrl(f);
      // Strip extension, match by normalised filename
      const stem = f.name.replace(/\.[^.]+$/, "");
      const matched = byNorm.get(norm(stem)) || null;
      next.push({ file: f, dataUrl, matchedVehicle: matched });
    }
    setItems(next);
  };

  const save = async () => {
    setBusy(true);
    let saved = 0, skipped = 0;
    const failed = [];
    for (const it of items) {
      if (!it.matchedVehicle || !it.dataUrl) { skipped++; continue; }
      try {
        const v = it.matchedVehicle;
        await api.put(`/vehicles/${v.id}`, { ...v, photo_url: it.dataUrl });
        saved++;
      } catch (e) {
        failed.push({ file: it.file.name, error: e?.response?.data?.detail || "Failed" });
      }
    }
    setResult({ saved, skipped, failed });
    setBusy(false);
    if (saved > 0) toast.success(`Saved ${saved} photo${saved === 1 ? "" : "s"}`);
    onDone?.();
  };

  const close = () => { reset(); onOpenChange(false); };
  const matchedCount = items.filter(i => i.matchedVehicle && !i.tooBig).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Images className="w-5 h-5 text-[#ea580c]" /> Bulk Vehicle Photos</DialogTitle>
        </DialogHeader>

        {!result && (
          <>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="text-sm font-semibold text-slate-800">How this works</div>
              <p className="text-xs text-slate-600 mt-1">
                Name each photo after the vehicle's number plate — e.g. <code className="px-1 py-0.5 rounded bg-white border">MH12AB1234.jpg</code>,
                <code className="px-1 py-0.5 rounded bg-white border ml-1">mh-12-ab-1234.webp</code>, or <code className="px-1 py-0.5 rounded bg-white border ml-1">MH 12 AB 1234.png</code>.
                We normalise spaces / dashes / case automatically. Existing photos on matched vehicles will be replaced.
              </p>
              <input ref={fileRef} type="file" accept="image/*" multiple webkitdirectory="" directory="" className="hidden" onChange={e => pickFiles(e.target.files)} data-testid="bulk-photos-folder-input" />
              <input type="file" accept="image/*" multiple className="hidden" id="bulk-photos-files" onChange={e => pickFiles(e.target.files)} data-testid="bulk-photos-files-input" />
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="bulk-photos-pick-folder"><FolderUp className="w-3.5 h-3.5 mr-1" /> Choose folder…</Button>
                <Button size="sm" variant="outline" onClick={() => document.getElementById("bulk-photos-files").click()} data-testid="bulk-photos-pick-files"><Upload className="w-3.5 h-3.5 mr-1" /> Choose images…</Button>
              </div>
            </div>

            {items.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-800">Preview ({items.length} file{items.length === 1 ? "" : "s"})</div>
                  <div className="text-xs">
                    <span className="text-emerald-700 font-semibold">{matchedCount} matched</span>
                    <span className="text-slate-400"> · </span>
                    <span className="text-rose-600 font-semibold">{items.length - matchedCount} unmatched</span>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1">
                  {items.map((it, i) => (
                    <div key={i} className={`flex items-center gap-3 p-2 rounded-lg border ${it.matchedVehicle ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40"}`} data-testid={`bulk-photo-row-${i}`}>
                      {it.dataUrl ? (
                        <img src={it.dataUrl} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded bg-rose-100 grid place-items-center text-rose-500 text-[10px] font-bold flex-shrink-0">TOO<br />BIG</div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-xs truncate">{it.file.name}</div>
                        {it.tooBig ? (
                          <div className="text-[11px] text-rose-700 mt-0.5">File over 3 MB — skipped</div>
                        ) : it.matchedVehicle ? (
                          <div className="text-[11px] text-emerald-800 mt-0.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> → <b>{it.matchedVehicle.vehicle_number}</b> ({it.matchedVehicle.vehicle_type})</div>
                        ) : (
                          <div className="text-[11px] text-rose-700 mt-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No vehicle matches this filename</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {result && (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-center" data-testid="bulk-photos-result">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <div className="mt-2 font-display font-bold text-emerald-900 text-lg">Photos uploaded</div>
            <div className="mt-1 text-sm text-emerald-800">
              <b>{result.saved}</b> saved · <b>{result.skipped}</b> skipped
              {result.failed.length > 0 && <> · <b className="text-rose-700">{result.failed.length}</b> failed</>}
            </div>
            {result.failed.length > 0 && (
              <div className="mt-3 text-left rounded-lg bg-white border border-rose-200 p-2 text-xs text-rose-800 max-h-40 overflow-auto">
                {result.failed.map((e, i) => <div key={i}>{e.file}: {e.error}</div>)}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {items.length > 0 && !result && (
            <Button onClick={save} disabled={busy || matchedCount === 0} className="bg-[#ea580c] hover:bg-orange-700" data-testid="bulk-photos-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              Save {matchedCount} photo{matchedCount === 1 ? "" : "s"}
            </Button>
          )}
          <Button variant="outline" onClick={close} data-testid="bulk-photos-close-btn"><X className="w-4 h-4 mr-1" /> {result ? "Close" : "Cancel"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
